import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useImportStore } from '@/stores/importStore';
import { ImportStep } from '@/features/channels/types/import';
import { useProvider } from '@/hooks/useProvider';
import { ImportForm } from './ImportForm';
import { ImportPreview } from './ImportPreview';
import { ImportProgress } from './ImportProgress';
import { StepIndicator } from '@/components/ui/step-indicator';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/queryKeys';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { toast } from 'sonner';

interface BulkImportModalProps {
  providerId: number;
  versionId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkImportModal({
  providerId,
  versionId,
  open,
  onOpenChange
}: BulkImportModalProps) {
  const { 
    currentStep,
    formData,
    previewData,
    errors,
    isImporting,
    currentSession,
    getNavigationState,
    goToPreviousStep,
    completeReset,
    startImport,
    resetImportState,
    setStep,
  } = useImportStore();
  
  // Use a ref to track if we're already closing the modal
  const isClosingRef = useRef(false);

  // Clear state when modal closes, but preserve for persistence
  useEffect(() => {
    // When modal opens, check if we have persisted state and use it
    if (open) {
      logger.info(LOG_CONTEXTS.IMPORT, 'Import modal opened, checking for persisted state');
    }
    
    // Only reset when explicitly closing (not when changing steps or during import)
    if (!open && !isClosingRef.current) {
      logger.info(LOG_CONTEXTS.IMPORT, 'Import modal closed, preserving state in localStorage');
      
      // Don't reset immediately on close - this allows state to persist
      // Only manually reset if requested by user action or on successful completion
      
      // If we were in the middle of an import, preserve the state entirely
      if (currentStep === 'progress' && currentSession) {
        // Do nothing - preserve state for when modal reopens
        logger.info(LOG_CONTEXTS.IMPORT, 'Preserving in-progress import state');
      }
    }
  }, [open, currentStep, currentSession]);

  const { data: provider } = useProvider(providerId);
  const queryClient = useQueryClient();

  // Handle the import completion process
  const handleComplete = useCallback(() => {
    logger.info(LOG_CONTEXTS.IMPORT, 'Import complete, preparing to close modal');
    
    // Mark that we're in the process of closing
    isClosingRef.current = true;
    
    // More aggressive approach to refresh channel data
    try {
      // First completely remove cached data
      queryClient.removeQueries({
        queryKey: queryKeys.channels.all
      });
      queryClient.removeQueries({
        queryKey: queryKeys.channelNumbers.all
      });
      
      // Then invalidate all queries to force a complete refresh
      queryClient.invalidateQueries({
        queryKey: queryKeys.channels.all,
        refetchType: 'all'
      });
      
      // Also specifically invalidate version-specific queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.channels.byVersion(providerId, versionId),
        refetchType: 'all'
      });
      
      logger.info(LOG_CONTEXTS.IMPORT, 'Forced cache invalidation on import completion');
    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Error refreshing data after import', { error });
    }
    
    // Close the modal after a short delay
    setTimeout(() => {
      onOpenChange(false);
      
      // Reset the closing flag and reset the store after modal is closed
      setTimeout(() => {
        isClosingRef.current = false;
        completeReset();
      }, 300);
    }, 1000);
  }, [onOpenChange, providerId, versionId, queryClient, completeReset]);

  const { canGoPrevious } = getNavigationState();

  // Memoize step components to prevent unnecessary rerenders
  const formComponent = useMemo(() => (
    <ImportForm 
      key="import-form-step"
      providerId={providerId}
      versionId={versionId}
      isSequential={provider?.has_sequence_numbers || false}
    />
  ), [providerId, versionId, provider?.has_sequence_numbers]);
  
  const previewComponent = useMemo(() => (
    <ImportPreview
      key="import-preview-step"
      providerId={providerId}
      versionId={versionId}
      data={previewData || []}
      errors={errors}
      isLoading={isImporting}
    />
  ), [providerId, versionId, previewData, errors, isImporting]);
  
  const progressComponent = useMemo(() => (
    currentSession ? (
      <ImportProgress
        key="import-progress-step"
        session={currentSession}
        providerId={providerId}
        versionId={versionId}
        onComplete={handleComplete}
        onError={(message) => {
          logger.error(LOG_CONTEXTS.IMPORT, 'Error during import', { message });
        }}
      />
    ) : null
  ), [currentSession, providerId, versionId, handleComplete]);
  
  // Render the current step content
  const renderStepContent = () => {
    logger.debug(LOG_CONTEXTS.IMPORT, 'Rendering step content for: ' + currentStep, {
      hasPreviewData: !!previewData,
      previewDataCount: previewData?.length || 0
    });
    
    switch (currentStep) {
      case 'form':
        return formComponent;
      case 'preview':
        return previewComponent;
      case 'progress':
        return progressComponent;
      default:
        return null;
    }
  };

  const steps = [
    { id: 'form', label: 'Import Data' },
    { id: 'preview', label: 'Preview' },
    { id: 'progress', label: 'Progress' }
  ];

  // Add a useEffect to handle successful import completion
  useEffect(() => {
    // Check if we've completed an import successfully
    if (
      currentStep === 'progress' && 
      currentSession?.status === 'completed' && 
      (!currentSession.error_log || currentSession.error_log.length === 0)
    ) {
      // Add a delay before resetting to allow the user to see the success message
      const timer = setTimeout(() => {
        // Reset the import state and close the modal
        resetImportState();
        setStep('form');
        
        // Show a success toast
        toast.success("All channels have been imported successfully.");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, currentSession, resetImportState, setStep, toast]);

  // Add a function to handle user-initiated reset
  const handleManualReset = useCallback(() => {
    logger.info(LOG_CONTEXTS.IMPORT, 'User manually reset import process');
    completeReset();
    // Force to form step
    setStep('form');
  }, [completeReset, setStep]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] h-auto overflow-y-auto flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <DialogTitle>Bulk Import Channels</DialogTitle>
            <DialogDescription>
              Import multiple channels at once for {provider?.name || 'current provider'}
            </DialogDescription>
          </div>
          
          {(currentStep !== 'form' || (formData && Object.keys(formData).length > 0)) && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleManualReset}
              className="ml-auto"
            >
              Reset Import
            </Button>
          )}
        </DialogHeader>
        
        <div className="mb-4">
          <StepIndicator
            steps={steps}
            currentStep={currentStep}
            className="mb-4"
          />
          
          <div className="flex justify-center space-x-4 mt-2">
            {currentStep !== 'form' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentIndex = steps.findIndex(s => s.id === currentStep);
                  if (currentIndex > 0) {
                    const prevStep = steps[currentIndex - 1].id as ImportStep;
                    setStep(prevStep);
                  }
                }}
              >
                Back to {currentStep === 'progress' ? 'Preview' : 'Form'}
              </Button>
            )}
          </div>
        </div>

        {/* Provider Type Instructions */}
        {currentStep === 'form' && provider && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {provider.has_sequence_numbers ? (
                <>
                  Enter channel names one per line. Numbers will be assigned automatically.
                  {formData?.lastNumber && (
                    <span className="block mt-1 font-medium">
                      Last used number: {formData.lastNumber}
                    </span>
                  )}
                </>
              ) : (
                <>
                  Enter channel data in the format: 
                  <span className="font-medium">
                    {formData.format === 'name_first' 
                      ? 'Name | Number 1 | Number 2'
                      : 'Number 1 | Number 2 | Name'
                    }
                  </span>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex-1 overflow-y-auto">
          {renderStepContent()}
        </div>

        {currentStep !== 'progress' && (
          <div className="flex justify-between items-center mt-4 pt-2 border-t">
            {currentStep === 'preview' ? (
              <>
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  disabled={isImporting}
                  size="sm"
                >
                  Back to Edit
                </Button>
                <Button
                  onClick={startImport}
                  disabled={isImporting || errors.length > 0}
                  size="sm"
                  className={errors.length > 0 ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {errors.length > 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Fix Errors First
                    </span>
                  ) : (
                    "Start Import Process"
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={goToPreviousStep}
                disabled={!canGoPrevious || isImporting}
                size="sm"
              >
                Previous
              </Button>
            )}
            <div className="w-[120px]" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
